// components/ChatInterface.tsx
import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Progress } from './ui/progress';
import { Separator } from './ui/separator';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './ui/accordion';
import { 
  Send, 
  Loader2, 
  RefreshCw, 
  CheckCircle, 
  Circle,
  Sparkles,
  Search,
  Globe,
  Database,
  ChevronLeft,
  ChevronRight,
  Brain
} from 'lucide-react';
import { motion } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { 
  initSocket, 
  initTemplateSocket, 
  sendMessageWS, 
  subscribe, 
  unsubscribe, 
  closeSocket 
} from '../services/socket';
import { cn } from '../lib/utils';
import { userDataApi } from '../services/data_apis';

interface Message {
  id: string;
  text: string;
  isUser: boolean;
  type?: string;
  data?: any;
  timestamp?: number;
}

interface ProgressUpdate {
  type: 'progress_update';
  stage: string;
  message: string;
  progress: number;
  timestamp: number;
  data?: any;
}

interface ThinkingState {
  isThinking: boolean;
  currentStage: string;
  currentMessage: string;
  stageHistory: Array<{
    stage: string;
    message: string;
    timestamp: number;
    completed: boolean;
  }>;
}

interface ClarificationQuestion {
  [key: string]: string;
}

type ResearchLength = 'quick_scan' | 'targeted' | 'deep_dive';

interface ChatInterfaceProps {
  initialQuery?: string;
  initialApiKey?: string;
  researchLength?: ResearchLength;
  sessionId?: string;
  resumeSession?: boolean;
  templateExecution?: boolean;
  sidebarCollapsed?: boolean;
}

const ChatInterface: React.FC<ChatInterfaceProps> = ({ initialQuery, initialApiKey, researchLength, sessionId, resumeSession, templateExecution, sidebarCollapsed = false }) => {
  
  // DEBUG: Log received props
  console.log('🎯 ChatInterface DEBUG - Received props:', {
    initialQuery,
    initialApiKey: initialApiKey ? 'PROVIDED' : 'NOT_PROVIDED',
    researchLength,
    sessionId,
    resumeSession,
    templateExecution
  });
  const { getAccessTokenSilently, loginWithRedirect, isAuthenticated } = useAuth0();
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingSession, setLoadingSession] = useState(false);
  const [initialQuerySent, setInitialQuerySent] = useState(false);
  const [progressInfo, setProgressInfo] = useState<ProgressUpdate | null>(null);
  const [awaitingClarification, setAwaitingClarification] = useState(false);
  const [, setClarificationQuestions] = useState<ClarificationQuestion>({});
  const [researchComplete, setResearchComplete] = useState(false);
  const [showRetryButton, setShowRetryButton] = useState(false);
  const [summaryVersions, setSummaryVersions] = useState<any[]>([]);
  const [thinkingState, setThinkingState] = useState<ThinkingState>({
    isThinking: false,
    currentStage: '',
    currentMessage: '',
    stageHistory: []
  });
  const [showThinking, setShowThinking] = useState<boolean>(() => {
    // Load preference from localStorage
    const saved = localStorage.getItem('showThinking');
    return saved !== null ? JSON.parse(saved) : false;
  });
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const originalQueryRef = useRef<string | null>(null);
  const disconnectionShownRef = useRef<boolean>(false);
  const errorShownRef = useRef<boolean>(false);
  const sendingRef = useRef<boolean>(false);
  const lastSentMessageRef = useRef<string>('');

  // Load existing session data
  const loadExistingSession = useCallback(async () => {
    if (!resumeSession || !sessionId || !isAuthenticated) return;
    
    try {
      setLoadingSession(true);
      const token = await getAccessTokenSilently();
      const response = await userDataApi.getSessionData(sessionId, token);
      
      if (response.success && response.data) {
        const { session, summary, topics } = response.data;
        
        // Create simple chat messages showing the completed research
        const sessionMessages: Message[] = [];
        
        // Add user's original query
        sessionMessages.push({
          id: '1',
          text: session.initial_query,
          isUser: true,
          timestamp: new Date(session.created_at).getTime()
        });
        
        // Add the research summary directly if available
        if (summary?.summary) {
          sessionMessages.push({
            id: '2',
            text: summary.summary,
            isUser: false,
            type: 'summary',
            data: {
              summary: summary.summary,
              topics: topics,
              research_length: session.research_length
            },
            timestamp: new Date(session.completed_at || session.updated_at).getTime()
          });
        } else {
          // If no summary, show a simple completion message
          sessionMessages.push({
            id: '2',
            text: 'Research session completed. You can ask follow-up questions about your research.',
            isUser: false,
            timestamp: new Date(session.completed_at || session.updated_at).getTime()
          });
        }
        
        setMessages(sessionMessages);
        setResearchComplete(true);
        setInitialQuerySent(true);
        
        // Set the original query for follow-up questions
        originalQueryRef.current = session.initial_query;
        
        // Store summary data for follow-up Q&A
        if (summary?.summary) {
          setSummaryVersions([{
            id: sessionId,
            summary: summary.summary,
            topics: topics,
            research_length: session.research_length,
            session_id: sessionId,
            timestamp: new Date(session.completed_at || session.updated_at).getTime()
          }]);
        }
      }
    } catch (error) {
      console.error('Failed to load existing session:', error);
      // Fall back to normal behavior - redirect to home or show error
      alert('Failed to load research session. Please try again.');
    } finally {
      setLoadingSession(false);
    }
  }, [resumeSession, sessionId, isAuthenticated, getAccessTokenSilently]);
  
  useEffect(() => {
    loadExistingSession();
  }, [loadExistingSession]);

  useEffect(() => {
    // Reset error flags for fresh chat session
    disconnectionShownRef.current = false;
    errorShownRef.current = false;
    
    // Only initialize socket if not already connected
    console.log('DEBUG: ChatInterface useEffect running, checking socket status');
    
    // Get JWT token for WebSocket authentication
    const initializeWebSocket = async () => {
      try {
        // Get the access token from Auth0 (works with or without audience)
        const token = await getAccessTokenSilently();
        console.log('DEBUG: Got JWT token for WebSocket connection');
        console.log('DEBUG: Token length:', token?.length);
        console.log('DEBUG: Token preview:', token?.substring(0, 50) + '...');
        
        if (!token) {
          throw new Error('No token received from Auth0');
        }
        
        // Initialize appropriate WebSocket based on execution type
        if (templateExecution && sessionId) {
          console.log('🎯 Template execution - using template WebSocket endpoint');
          console.log('🎯 Template execution sessionId:', sessionId);
          initSocket(token, true); // true = use template endpoint (/template-execution)
        } else {
          console.log('🔄 Regular research - using multi-agent WebSocket endpoint');
          initSocket(token, false); // false = use regular endpoint (/multi-agent-chat)
        }
      } catch (error: any) {
        console.error('DEBUG: Failed to get JWT token for WebSocket:', error);
        
        // Check if it's a login_required error (token expired)
        if (error.error === 'login_required' || error.error === 'consent_required') {
          console.log('Token expired, redirecting to login...');
          // Clear any stored auth state
          localStorage.removeItem('auth0.is.authenticated');
          // Redirect to login
          loginWithRedirect();
          return;
        }
        
        // Show user-friendly error for other cases
        const errorMessage = {
          id: Date.now().toString(),
          text: 'Authentication session expired. Redirecting to login...',
          isUser: false,
          type: 'error',
          timestamp: Date.now()
        };
        setMessages([errorMessage]);
        
        // Redirect to login after a short delay
        setTimeout(() => {
          loginWithRedirect();
        }, 2000);
        
        // Don't try to connect without token
        return;
      }
    };
    
    initializeWebSocket();
  
    const handleMessage = (data: any) => {
      console.log('Received WebSocket message:', data);
      console.log('Message type:', data?.type);
      console.log('Message data:', data?.data);
      
      // Handle WebSocket connection
      if (data.type === 'websocket_connected') {
        console.log('WebSocket connected successfully');
        // Reset flags when reconnected
        disconnectionShownRef.current = false;
        errorShownRef.current = false;
        return;
      }
      
      // Handle WebSocket disconnection
      if (data.type === 'websocket_disconnected') {
        console.log('WebSocket disconnected:', data);
        
        // Prevent duplicate disconnection messages
        if (disconnectionShownRef.current) {
          console.log('Disconnection message already shown, skipping duplicate');
          return;
        }
        
        // Don't show disconnection errors if chat is empty (fresh start)
        if (messages.length === 0) {
          console.log('Skipping disconnection message for empty chat');
          return;
        }
        
        disconnectionShownRef.current = true;
        
        // Clear thinking state immediately
        setThinkingState({
          isThinking: false,
          currentStage: '',
          currentMessage: '',
          stageHistory: []
        });
        
        // Clear loading state
        setIsLoading(false);
        setAwaitingClarification(false);
        setProgressInfo(null);
        
        // Add disconnection message to chat
        const disconnectionMessage = {
          id: Date.now().toString(),
          text: data.wasClean 
            ? 'Connection closed. Please refresh to continue.' 
            : 'Connection lost. The research may have been interrupted. Please refresh to try again.',
          isUser: false,
          type: 'error',
          data: data,
          timestamp: Date.now()
        };
        
        setMessages(currentMessages => [...currentMessages, disconnectionMessage]);
        return;
      }
      
      // Handle WebSocket authentication errors
      if (data.type === 'websocket_error' && data.error?.includes('Authentication')) {
        console.log('WebSocket authentication error, redirecting to login');
        // Clear auth state and redirect to login
        localStorage.removeItem('auth0.is.authenticated');
        loginWithRedirect();
        return;
      }
      
      // Handle WebSocket errors
      if (data.type === 'websocket_error') {
        console.log('WebSocket error:', data);
        
        // Prevent duplicate error messages
        if (errorShownRef.current) {
          console.log('Error message already shown, skipping duplicate');
          return;
        }
        
        // Don't show connection errors if chat is empty (fresh start)
        if (messages.length === 0) {
          console.log('Skipping connection error for empty chat');
          return;
        }
        
        errorShownRef.current = true;
        
        // Clear thinking state on error
        setThinkingState({
          isThinking: false,
          currentStage: '',
          currentMessage: '',
          stageHistory: []
        });
        
        // Clear loading state
        setIsLoading(false);
        setProgressInfo(null);
        
        // Add error message to chat
        const errorMessage = {
          id: Date.now().toString(),
          text: 'Connection error occurred. Please check your internet connection and refresh to try again.',
          isUser: false,
          type: 'error',
          data: data,
          timestamp: Date.now()
        };
        
        setMessages(currentMessages => [...currentMessages, errorMessage]);
        return;
      }
      
      // Handle progress updates - Update thinking state
      if (data.type === 'progress_update') {
        setProgressInfo(data);
        
        // Log different progress handling for template vs regular research
        if (templateExecution) {
          console.log('🎯 Template execution progress:', data.stage, data.message);
        } else {
          console.log('🔄 Regular research progress:', data.stage, data.message);
        }
        
        // Update thinking state with detailed progress
        setThinkingState(prev => {
          const newStageHistory = [...prev.stageHistory];
          
          // Mark previous stage as completed if we're moving to a new stage
          if (prev.currentStage && prev.currentStage !== data.stage) {
            const lastStageIndex = newStageHistory.findIndex(s => s.stage === prev.currentStage);
            if (lastStageIndex >= 0) {
              newStageHistory[lastStageIndex].completed = true;
            }
          }
          
          // Add new stage if it doesn't exist
          const existingStageIndex = newStageHistory.findIndex(s => s.stage === data.stage);
          if (existingStageIndex === -1) {
            newStageHistory.push({
              stage: data.stage,
              message: data.message,
              timestamp: data.timestamp || Date.now(),
              completed: false
            });
          } else {
            // Update existing stage
            newStageHistory[existingStageIndex].message = data.message;
            newStageHistory[existingStageIndex].timestamp = data.timestamp || Date.now();
          }
          
          return {
            isThinking: true,
            currentStage: data.stage,
            currentMessage: data.message,
            stageHistory: newStageHistory
          };
        });
        
        return;
      }
      
      // Filter out user message echoes - frontend already adds user messages immediately
      if (data.type === 'user_message' || data.type === 'user_query' || 
          (data.type === 'chat_response' && data.isUser)) {
        console.log('Filtered out user message echo:', data);
        return;
      }
      
      // Handle clarification questions
      if (data.type === 'clarifying_questions') {
        // Template executions should NOT receive clarification questions as they have predefined parameters
        if (templateExecution) {
          console.log('🎯 WARNING: Template execution received clarification questions - this should not happen');
          console.log('🎯 Template execution should skip clarification phase');
          return; // Skip clarification for template execution
        }
        
        setAwaitingClarification(true);
        const questions = data.questions || data.data?.questions || data.data || {};
        
        // Store original query in questions for later use
        setMessages(currentMessages => {
          const lastUserMessage = currentMessages.filter(m => m.isUser).pop();
          if (lastUserMessage) {
            originalQueryRef.current = lastUserMessage.text;
          }
          
          setClarificationQuestions(questions);
          
          // Pause thinking state for clarification - completely stop thinking box
          setAwaitingClarification(true);
          setThinkingState({
            isThinking: false,
            currentStage: '',
            currentMessage: '',
            stageHistory: []
          });
          
          const clarificationMessage: Message = {
            id: Date.now().toString(),
            text: data.message || "I need some clarification to provide you with the best research:",
            isUser: false,
            type: 'clarifying_questions',
            data: questions, // Questions will be rendered by renderMessageContent
            timestamp: Date.now()
          };
          
          // Check for duplicate clarification messages
          const isDuplicate = currentMessages.some(msg => 
            msg.type === 'clarifying_questions' && 
            Date.now() - (msg.timestamp || 0) < 3000
          );
          
          if (!isDuplicate) {
            return [...currentMessages, clarificationMessage];
          }
          return currentMessages;
        });
        
        setIsLoading(false);
        return;
      }
      
      // Handle summary versions FIRST - before adding to messages
      if (data.type === 'research_complete' || data.type === 'summary_retry_complete') {
        setResearchComplete(true);
        setShowRetryButton(true); // Show retry button after research output
        
        // Add to summary versions
        setSummaryVersions(prev => {
          const newVersion = {
            ...data,
            // Extract summary content from nested data if available
            summary_content: data.data?.summary_content || data.data?.summary || data.summary_content || data.summary || data.message,
            followup_instruction: data.data?.followup_instruction || data.followup_instruction || "You can now ask me specific questions about any of the researched topics.",
            session_id: data.data?.session_id || data.session_id,
            timestamp: Date.now(),
            isRetry: data.type === 'summary_retry_complete'
          };
          return [...prev, newVersion];
        });
        
        setIsLoading(false);
        setAwaitingClarification(false);
        setProgressInfo(null);
        
        // Clear thinking state when summary is received
        setThinkingState({
          isThinking: false,
          currentStage: '',
          currentMessage: '',
          stageHistory: []
        });
        
        // Don't add summary messages to the main messages array - they'll be handled by SummaryVersions component
        return;
      }
      
      // Handle different response types for non-summary messages
      let messageText = '';
      let messageType = data.type || 'chat_response';
      
      // Log message type for debugging
      const executionContext = templateExecution ? 'template execution' : 'regular research';
      console.log(`📨 Message received for ${executionContext}:`, data.type);
      
      if (data.type === 'query_validation_response') {
        messageText = data.message || 'Please provide a clearer research question.';
      } else if (data.type === 'qa_response') {
        messageText = data.answer || 'Answer provided based on research data';
      } else if (data.type === 'comprehensive_research_result') {
        messageText = 'Comprehensive research completed! Here are the detailed findings:';
      } else if (data.type === 'knowledge_based_response') {
        messageText = data.data || 'Response generated from knowledge base';
      } else if (data.type === 'chat_response') {
        messageText = data.data || 'Response generated';
      } else if (data.type === 'template_execution_progress') {
        // Special handling for template execution progress
        messageText = data.message || 'Template execution in progress...';
        console.log('🎯 Template execution progress message:', messageText);
      } else if (data.type === 'template_execution_complete') {
        // Special handling for template execution completion
        messageText = data.message || 'Template execution completed successfully!';
        console.log('🎯 Template execution completed:', messageText);
      } else if (typeof data.data === 'string') {
        messageText = data.data;
      } else if (data.data) {
        messageText = 'Response generated successfully!';
      } else {
        messageText = JSON.stringify(data);
      }
      
      const newMessage: Message = {
        id: Date.now().toString(),
        text: messageText,
        isUser: false,
        type: messageType,
        data: data,
        timestamp: Date.now()
      };
      
      // Improved duplicate detection using functional state update
      setMessages(currentMessages => {
        // Check for exact duplicates
        const isDuplicate = currentMessages.some(msg => 
          msg.text === messageText && 
          msg.type === messageType && 
          Date.now() - (msg.timestamp || 0) < 3000  // Reduced to 3 seconds
        );
        
        // Additional check: prevent any message that matches recent user messages
        const recentUserMessage = currentMessages
          .filter(msg => msg.isUser)
          .slice(-2) // Check last 2 user messages
          .find(msg => msg.text === messageText);
        
        if (recentUserMessage) {
          console.log('Prevented duplicate user message:', messageText);
          return currentMessages;
        }
        
        if (!isDuplicate) {
          return [...currentMessages, newMessage];
        }
        return currentMessages;
      });
      
      setIsLoading(false);
      // Only clear awaiting clarification if this is not a clarification question message
      if (data.type !== 'clarifying_questions') {
        setAwaitingClarification(false);
      }
      setProgressInfo(null);
      
      // Clear thinking state when final response is received
      if (data.type === 'qa_response' || data.type === 'query_validation_response' || data.type === 'error') {
        setThinkingState({
          isThinking: false,
          currentStage: '',
          currentMessage: '',
          stageHistory: []
        });
      }
      
      // Hide retry button when receiving chatData responses (follow-up Q&A)
      if (data.type === 'qa_response') {
        setShowRetryButton(false); // Hide retry after chatData usage
      }
      
      // Reset research complete flag when starting new research
      if (data.type === 'clarifying_questions' || data.type === 'query_validation_response') {
        setResearchComplete(false);
        setShowRetryButton(false);
        setSummaryVersions([]); // Clear previous summary versions
      }
    };
  
    subscribe(handleMessage);
  
    return () => unsubscribe(handleMessage);
  }, []);  // Keep empty dependency array

  // Cleanup WebSocket connection when component unmounts
  useEffect(() => {
    return () => {
      closeSocket();
    };
  }, []);

  // Separate useEffect for initial query to avoid re-subscribing
  useEffect(() => {
    // Don't auto-send initial query if we're resuming a session
    if (resumeSession && sessionId) {
      console.log('🎯 SKIPPING auto-send - resuming session:', sessionId);
      return;
    }
    
    // For template execution, we DO want to send the initial query WITH the session ID
    // This will skip topic/question generation and go straight to answer research
    if (templateExecution && sessionId) {
      console.log('🎯 Template execution detected - will auto-send initial query with session ID');
    }
    
    // Use a timeout to ensure this only runs once even in StrictMode
    let timeout: NodeJS.Timeout;
    
    if (initialQuery && !initialQuerySent) {
      // Delay slightly to ensure single execution
      timeout = setTimeout(() => {
        if (!initialQuerySent) {
          handleSend(initialQuery);
          setInitialQuerySent(true);
        }
      }, 100);
    }
    
    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [initialQuery, initialQuerySent, resumeSession, sessionId]); // Include dependencies to prevent re-runs

  useEffect(() => {
    scrollToBottom();
  }, [messages, progressInfo]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const toggleThinking = () => {
    const newValue = !showThinking;
    setShowThinking(newValue);
    localStorage.setItem('showThinking', JSON.stringify(newValue));
  };

  // Memoize computed values to prevent unnecessary re-renders
  const isInputEmpty = useMemo(() => !input.trim(), [input]);
  const isSendDisabled = useMemo(() => isLoading || isInputEmpty, [isLoading, isInputEmpty]);

  // Memoize event handlers
  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInput(e.target.value);
  }, []);

  const handleSend = useCallback(async (msg?: string | React.MouseEvent) => {
    console.log('DEBUG: handleSend called with:', msg);
    const text = typeof msg === 'string' ? msg : input;
    console.log('DEBUG: Extracted text:', text);
    console.log('DEBUG: Text trimmed length:', text.trim().length);
    if (!text.trim()) {
      console.log('DEBUG: Empty text, returning early');
      return;
    }

    // Prevent duplicate sends
    if (sendingRef.current && lastSentMessageRef.current === text) {
      console.log('DEBUG: Preventing duplicate send of:', text);
      return;
    }
    
    // Hide retry button when user sends any new message
    setShowRetryButton(false);
    
    console.log('DEBUG: Setting sending flags');
    sendingRef.current = true;
    lastSentMessageRef.current = text;
    
    // Reset sending flag after a short delay
    setTimeout(() => {
      sendingRef.current = false;
    }, 500);

    // Add user message immediately to UI
    console.log('DEBUG: Creating user message object');
    const userMessage: Message = {
      id: Date.now().toString(),
      text: text,
      isUser: true,
      timestamp: Date.now()
    };
    console.log('DEBUG: User message created:', userMessage);
    
    console.log('DEBUG: Adding user message to chat');
    setMessages(currentMessages => [...currentMessages, userMessage]);
  
    setIsLoading(true);
    
    // Only show thinking state if research is not complete or awaiting clarification
    if (!researchComplete || awaitingClarification) {
      const thinkingMessage = templateExecution 
        ? 'Executing template research - analyzing predefined topics...' 
        : 'Processing your request...';
      
      setThinkingState(prev => ({
        ...prev,
        isThinking: true,
        currentMessage: thinkingMessage
      }));
    }
  
    console.log('DEBUG: Creating payload object');
    
    // Get JWT token for authentication
    let authToken: string | undefined;
    try {
      authToken = await getAccessTokenSilently();
      console.log('DEBUG: JWT token obtained successfully');
    } catch (error) {
      console.error('DEBUG: Failed to get JWT token:', error);
      // Continue without token - backend will handle the error
    }
    
    let payload: any = {
      message: text,
      type: 'query'
    };
    
    // SECURITY: Include JWT token for authentication
    if (authToken) {
      payload.jwt_token = authToken;
    }
    
    // SECURITY: Include API key if provided
    if (initialApiKey) {
      payload.api_key = initialApiKey;
    }
    
    // Include research length parameter
    if (researchLength) {
      payload.research_length = researchLength;
    }
    
    // Include session ID and template execution flag for template execution
    console.log('🎯 WebSocket DEBUG - Template execution check:', {
      templateExecution,
      sessionId,
      willIncludeSessionId: templateExecution && sessionId
    });
    
    if (templateExecution && sessionId) {
      payload.session_id = sessionId;
      payload.template_execution = true;
      payload.execution_type = 'template'; // Explicit flag for backend routing
      console.log('🎯 WebSocket DEBUG - Added template execution data to payload:', {
        sessionId,
        templateExecution: true,
        executionType: 'template'
      });
    } else {
      payload.execution_type = 'regular'; // Explicit flag for regular research
      console.log('🔄 WebSocket DEBUG - Regular research payload');
    }
    
    console.log('DEBUG: Final payload being sent:', payload);
  
    if (awaitingClarification) {
      // Send the clarification answer as user_response
      const answers = { user_response: text };
  
      payload = {
        message: text, // Send the answer as the message
        type: 'clarification_response',
        clarification_answers: answers,
      };
      
      // SECURITY: Include JWT token for authentication
      if (authToken) {
        payload.jwt_token = authToken;
      }
      
      // SECURITY: Include API key in clarification responses too
      if (initialApiKey) {
        payload.api_key = initialApiKey;
      }
      
      // Include research length parameter in clarification responses too
      if (researchLength) {
        payload.research_length = researchLength;
      }
      
      // Clear the clarification state immediately since we're submitting
      setAwaitingClarification(false);
      setClarificationQuestions({});
    }
  
    console.log('DEBUG: About to call sendMessageWS with payload:', payload);
    console.log('DEBUG: Calling sendMessageWS...');
    sendMessageWS(payload);
    console.log('DEBUG: sendMessageWS call completed');
    
    // Clear input immediately
    setInput('');
    console.log('DEBUG: Input cleared, handleSend function complete');
  }, [input, researchComplete, awaitingClarification, thinkingState, sendingRef, lastSentMessageRef]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }, [handleSend]);

  const ThinkingComponent = () => {
    if (!thinkingState.isThinking) return null;
    
    const getStageDisplayName = (stage: string) => {
      // Different stage names for template execution vs regular research
      const templateStageNames: {[key: string]: string} = {
        'initialization': 'Initializing template execution',
        'template_loading': 'Loading template configuration',
        'answer_research': 'Researching predefined topics',
        'template_processing': 'Processing template questions',
        'summary_generation': 'Creating template summary',
        'completion': 'Completing template execution'
      };
      
      const regularStageNames: {[key: string]: string} = {
        'initialization': 'Initializing',
        'clarification_check': 'Checking clarification needs',
        'topic_generation': 'Generating research topics',
        'question_generation': 'Generating questions for topics',
        'answer_research': 'Researching answers',
        'summary_generation': 'Creating summary',
        'completion': 'Completing research'
      };
      
      const stageNames = templateExecution ? templateStageNames : regularStageNames;
      return stageNames[stage] || stage.replace('_', ' ');
    };
    
    if (awaitingClarification) return null;

    // Show collapsed indicator when thinking is hidden
    if (!showThinking) {
      return (
        <div className="mb-4">
          <div className="flex items-center space-x-2 px-4 py-2 bg-blue-50/50 dark:bg-blue-950/30 rounded-lg border border-blue-200/50 dark:border-blue-800/50">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
            >
              <Brain className="w-4 h-4 text-blue-600 dark:text-blue-400" />
            </motion.div>
            <span className="text-sm text-blue-700 dark:text-blue-300">
              {thinkingState.currentMessage || 'Processing your request...'}
            </span>
            <button
              onClick={toggleThinking}
              className="ml-auto text-xs text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 underline"
            >
              Show details
            </button>
          </div>
        </div>
      );
    }

    // Show full thinking component when expanded
    return (
      <div className="mb-6">
        <Card className="border-primary/20 bg-gradient-to-r from-blue-50/80 to-indigo-50/80 dark:from-blue-950/60 dark:to-indigo-950/60 backdrop-blur-sm shadow-md">
          <CardContent className="p-6">
            <div className="flex items-center space-x-3 mb-4">
              <div className="relative">
                <Brain className="w-6 h-6 text-primary" />
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                  className="absolute -top-1 -right-1"
                >
                  <Sparkles className="w-3 h-3 text-amber-500" />
                </motion.div>
              </div>
              <div>
                <h3 className="font-semibold text-lg text-stone-900 dark:text-slate-100">
                  {templateExecution ? 'AI Template Execution in Progress' : 'AI Research in Progress'}
                </h3>
                <p className="text-sm text-stone-600 dark:text-slate-300">{thinkingState.currentMessage}</p>
              </div>
            </div>
            
            {/* Timeline */}
            {thinkingState.stageHistory.length > 0 && (
              <div className="space-y-3 mt-6">
                <Separator />
                <div className="relative pl-6">
                  {/* Vertical line */}
                  <div className="absolute left-2 top-2 bottom-2 w-0.5 bg-border" />
                  
                  {thinkingState.stageHistory.map((stage, idx) => {
                    const isActive = stage.stage === thinkingState.currentStage;
                    const isCompleted = stage.completed;
                    
                    return (
                      <div
                        key={idx}
                        className="relative flex items-center pb-3 last:pb-0"
                      >
                        {/* Timeline dot */}
                        <div className={cn(
                          "absolute -left-6 w-4 h-4 rounded-full border-2 border-background z-10 flex items-center justify-center",
                          isCompleted 
                            ? "bg-green-500" 
                            : isActive 
                              ? "bg-primary animate-pulse" 
                              : "bg-muted"
                        )}>
                          {isCompleted ? (
                            <CheckCircle className="w-2 h-2 text-white" />
                          ) : isActive ? (
                            <div className="animate-pulse">
                              <Circle className="w-2 h-2 text-primary-foreground" />
                            </div>
                          ) : (
                            <Circle className="w-2 h-2 text-muted-foreground" />
                          )}
                        </div>
                        
                        {/* Stage content */}
                        <div className="flex-1 min-w-0">
                          <p className={cn(
                            "text-sm font-medium",
                            isCompleted 
                              ? "text-green-600 dark:text-green-400" 
                              : isActive 
                                ? "text-primary" 
                                : "text-muted-foreground"
                          )}>
                            {getStageDisplayName(stage.stage)}
                          </p>
                          {isActive && (
                            <div className="flex items-center mt-1 space-x-2">
                              <Loader2 className="w-3 h-3 animate-spin text-primary" />
                              <span className="text-xs text-muted-foreground">Processing...</span>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  };

  const SummaryVersions = ({ versions }: { versions: any[] }) => {
    const [currentVersionIndex, setCurrentVersionIndex] = useState(versions.length - 1); // Show latest by default
    
    // Reset to latest version when new versions are added
    useEffect(() => {
      setCurrentVersionIndex(versions.length - 1);
    }, [versions.length]);
    
    if (versions.length === 0) return null;
    
    const currentVersion = versions[currentVersionIndex];
    const summary = currentVersion.summary_content || currentVersion.summary || currentVersion.message || '';
    const followupInstruction = currentVersion.followup_instruction || "You can now ask me specific questions about any of the researched topics.";
    const sessionId = currentVersion.session_id;
    
    const handleSummaryRetry = async () => {
      if (sessionId) {
        // Set thinking state to show status board during retry
        setThinkingState({
          isThinking: true,
          currentStage: 'summary_retry',
          currentMessage: 'Regenerating research summary...',
          stageHistory: [{
            stage: 'summary_retry',
            message: 'Regenerating research summary...',
            timestamp: Date.now(),
            completed: false
          }]
        });
        
        setIsLoading(true);
        
        // Get JWT token for authentication
        let authToken: string | undefined;
        try {
          authToken = await getAccessTokenSilently();
          console.log('DEBUG: JWT token obtained for retry');
        } catch (error) {
          console.error('DEBUG: Failed to get JWT token for retry:', error);
        }
        
        const retryPayload: any = {
          message: "retry_summary",
          type: "retry_summary",
          session_id: sessionId
        };
        
        // SECURITY: Include JWT token for authentication
        if (authToken) {
          retryPayload.jwt_token = authToken;
        }
        
        // SECURITY: Include API key in retry requests
        if (initialApiKey) {
          retryPayload.api_key = initialApiKey;
        }
        
        console.log('Retrying summary generation for session:', sessionId);
        sendMessageWS(retryPayload);
      }
    };

    const goToPrevious = () => {
      setCurrentVersionIndex(Math.max(0, currentVersionIndex - 1));
    };

    const goToNext = () => {
      setCurrentVersionIndex(Math.min(versions.length - 1, currentVersionIndex + 1));
    };

    return (
      <div className="space-y-4">
        <Card className="shadow-md border-0 bg-white dark:bg-gray-800">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-gray-800 dark:text-gray-200">
                Research Complete
              </CardTitle>
              {versions.length > 1 && (
                <div className="flex items-center space-x-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={goToPrevious}
                    disabled={currentVersionIndex === 0}
                    className="h-8 w-8 p-0"
                    title="Previous version"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span className="text-sm text-gray-500 dark:text-gray-400 px-2">
                    {currentVersionIndex + 1}/{versions.length}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={goToNext}
                    disabled={currentVersionIndex === versions.length - 1}
                    className="h-8 w-8 p-0"
                    title="Next version"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <div className="prose prose-sm max-w-none dark:prose-invert">
              <MarkdownRenderer content={summary} />
            </div>
            <div className="mt-6 pt-4 border-t">
              <div className="flex items-center justify-between">
                <p className="text-xs text-gray-400 dark:text-gray-500">
                  {followupInstruction}
                </p>
                {showRetryButton && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleSummaryRetry}
                    className="text-xs"
                    title="Retry summary generation"
                  >
                    <RefreshCw className="w-3 h-3 mr-1" />
                    Retry Summary
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  const ResearchCompleteResult = ({ data }: { data: any }) => {
    const summary = data.summary_content || data.summary || data.message || '';
    const followupInstruction = data.followup_instruction || "You can now ask me specific questions about any of the researched topics.";
    const sessionId = data.session_id;
    
    const handleSummaryRetry = async () => {
      if (sessionId) {
        // Get JWT token for authentication
        let authToken: string | undefined;
        try {
          authToken = await getAccessTokenSilently();
          console.log('DEBUG: JWT token obtained for retry');
        } catch (error) {
          console.error('DEBUG: Failed to get JWT token for retry:', error);
        }
        
        const retryPayload: any = {
          message: "retry_summary",
          type: "retry_summary",
          session_id: sessionId
        };
        
        // SECURITY: Include JWT token for authentication
        if (authToken) {
          retryPayload.jwt_token = authToken;
        }
        
        // SECURITY: Include API key in retry requests
        if (initialApiKey) {
          retryPayload.api_key = initialApiKey;
        }
        
        console.log('Retrying summary generation for session:', sessionId);
        sendMessageWS(retryPayload);
      }
    };
    
    return (
      <div className="space-y-4">
        <Card className="shadow-md border-0 bg-white dark:bg-gray-800">
          <CardHeader className="pb-3">
            <CardTitle className="text-gray-800 dark:text-gray-200">
              Research Complete
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="prose prose-sm max-w-none dark:prose-invert">
              <MarkdownRenderer content={summary} />
            </div>
            <div className="mt-6 pt-4 border-t">
              <div className="flex items-center justify-between">
                <p className="text-xs text-gray-400 dark:text-gray-500">
                  {followupInstruction}
                </p>
                {showRetryButton && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleSummaryRetry}
                    className="text-xs"
                    title="Retry summary generation"
                  >
                    <RefreshCw className="w-3 h-3 mr-1" />
                    Retry Summary
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  const QueryValidationResult = ({ data, text }: { data: any; text: string }) => {
    const examples = data?.examples || [];
    
    return (
      <div className="space-y-4">
        <Card className="shadow-md border-0 bg-white dark:bg-gray-800">
          <CardContent className="p-6">
            <p className="text-gray-700 dark:text-gray-200 mb-4">{text}</p>
            
            {examples.length > 0 && (
              <div className="space-y-3">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-300">Try asking research questions like these:</p>
                <div className="space-y-2">
                  {examples.map((example: string, index: number) => (
                    <div key={index} className="pl-4">
                      <p className="text-sm text-gray-600 dark:text-gray-400">• "{example}"</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  };

  const QAResponseResult = ({ data }: { data: any }) => {
    const answer = data.answer || '';
    const relatedTopics = data.related_topics || [];
    
    return (
      <div className="space-y-4">
        {/* Answer */}
        <Card className="shadow-md border-0 bg-white dark:bg-gray-800">
          <CardContent className="p-6">
            <div className="prose prose-sm max-w-none dark:prose-invert">
              <MarkdownRenderer content={answer} />
            </div>
          </CardContent>
        </Card>

        {/* Related Topics */}
        {relatedTopics.length > 0 && (
          <Card className="border-slate-200 dark:border-slate-700">
            <CardContent className="p-4">
              <h4 className="text-sm font-semibold text-gray-600 dark:text-gray-400 mb-3">
                Related Topics
              </h4>
              <div className="flex flex-wrap gap-2">
                {relatedTopics.map((topic: string, index: number) => (
                  <span 
                    key={index}
                    className="inline-flex items-center px-3 py-1 rounded-full text-xs bg-secondary text-secondary-foreground border hover:bg-secondary/80 transition-colors"
                  >
                    {topic}
                  </span>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    );
  };

  const ComprehensiveResearchResult = ({ data }: { data: any }) => {
    // Handle nested data structure from backend
    const researchData = data.data?.data || data.data || {};
    const mainTopics = researchData.main_topics || [];
    const recursiveTopics = researchData.recursive_topics || [];
    const metrics = researchData.research_metrics || {};
    
    return (
      <div className="space-y-6">
        {/* Executive Summary */}
        <Card className="shadow-md border-0 bg-white dark:bg-gray-800">
          <CardHeader>
            <CardTitle className="text-gray-800 dark:text-gray-200">
              Executive Summary
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="prose prose-sm max-w-none dark:prose-invert">
              <MarkdownRenderer content={researchData.executive_summary || 'Research completed successfully!'} />
            </div>
          </CardContent>
        </Card>

        {/* Research Metrics */}
        <Card className="shadow-md border-0 bg-white dark:bg-gray-800">
          <CardContent className="p-4">
            <div className="flex items-center space-x-2 mb-3">
              <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
              <h3 className="font-semibold text-green-800 dark:text-green-200">Research Metrics</h3>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div className="flex flex-col">
                <span className="text-muted-foreground">Topics Researched</span>
                <span className="font-bold text-lg">{metrics.total_topics_researched || 0}</span>
              </div>
              <div className="flex flex-col">
                <span className="text-muted-foreground">Discoveries</span>
                <span className="font-bold text-lg">{metrics.recursive_topics_discovered || 0}</span>
              </div>
              <div className="flex flex-col">
                <span className="text-muted-foreground">Questions Answered</span>
                <span className="font-bold text-lg">{metrics.total_questions_answered || 0}</span>
              </div>
              <div className="flex flex-col">
                <span className="text-muted-foreground">Max Depth</span>
                <span className="font-bold text-lg">{metrics.research_depth_achieved || 1}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Main Research Topics */}
        <div>
          <h3 className="text-lg font-semibold mb-4 flex items-center space-x-2">
            <Search className="w-5 h-5 text-primary" />
            <span>Main Research Topics</span>
          </h3>
          
          <Accordion type="multiple" className="space-y-2">
            {mainTopics.map((topic: any, index: number) => (
              <AccordionItem key={index} value={`topic-${index}`} className="border rounded-lg">
                <AccordionTrigger className="px-4 hover:no-underline">
                  <div className="flex items-center justify-between w-full mr-4">
                    <span className="font-medium text-left">{topic.title}</span>
                    <span className="text-xs bg-primary/10 text-primary px-2 py-1 rounded-full">
                      {topic.questions_count || 0} questions
                    </span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-4 pb-4">
                  <div>
                    <h4 className="font-medium text-sm text-primary mb-2">Key Findings:</h4>
                    <div className="space-y-2">
                      {topic.key_findings && topic.key_findings.map((finding: string, fidx: number) => (
                        <div key={fidx} className="flex items-start space-x-2 text-sm">
                          <Circle className="w-2 h-2 mt-2 text-muted-foreground flex-shrink-0" />
                          <div className="prose prose-sm max-w-none dark:prose-invert">
                            <MarkdownRenderer content={finding} />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>

        {/* Recursive Research Topics */}
        {recursiveTopics.length > 0 && (
          <div>
            <h3 className="text-lg font-semibold mb-2 flex items-center space-x-2">
              <Globe className="w-5 h-5 text-secondary" />
              <span>Related Topics Discovered</span>
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              These topics were automatically discovered during research and explored for additional insights.
            </p>
            
            <Accordion type="multiple" className="space-y-2">
              {recursiveTopics.map((topic: any, index: number) => (
                <AccordionItem key={index} value={`recursive-${index}`} className="border rounded-lg bg-sky-50/50 dark:bg-sky-950/20">
                  <AccordionTrigger className="px-4 hover:no-underline">
                    <div className="flex items-center justify-between w-full mr-4">
                      <span className="font-medium text-left">{topic.title}</span>
                      <span className="text-xs bg-sky-100 dark:bg-sky-900 text-sky-800 dark:text-sky-200 px-2 py-1 rounded-full">
                        Depth {topic.depth}
                      </span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-4 pb-4">
                    <div className="space-y-3">
                      <p className="text-sm text-muted-foreground">
                        <strong>Parent topic:</strong> {topic.parent}
                      </p>
                      <div>
                        <h4 className="font-medium text-sm text-primary mb-2">Findings:</h4>
                        <div className="space-y-2">
                          {topic.findings && topic.findings.map((finding: string, fidx: number) => (
                            <div key={fidx} className="flex items-start space-x-2 text-sm">
                              <Circle className="w-2 h-2 mt-2 text-muted-foreground flex-shrink-0" />
                              <div className="prose prose-sm max-w-none dark:prose-invert">
                                <MarkdownRenderer content={finding} />
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        )}

        {/* Session Information */}
        {data.session_id && (
          <Card className="border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50">
            <CardContent className="p-4">
              <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                <Database className="w-4 h-4" />
                <div>
                  <strong>Research Session ID:</strong> {data.session_id}
                  <br />
                  This research has been saved to the knowledge base for future reference.
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    );
  };

  const MarkdownRenderer = ({ content }: { content: string }) => (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        code({ inline, className, children, ...props }: any) {
          return inline ? (
            <code
              className="px-1 py-0.5 bg-muted text-sm rounded font-mono"
              {...props}
            >
              {children}
            </code>
          ) : (
            <pre className="bg-muted border rounded-lg p-3 overflow-auto my-4 text-sm font-mono">
              <code>{children}</code>
            </pre>
          );
        },
        p: ({ children }) => (
          <p className="mb-4 leading-relaxed">
            {children}
          </p>
        ),
        h1: ({ children }) => (
          <h1 className="text-2xl font-bold mb-4 mt-6 text-foreground">
            {children}
          </h1>
        ),
        h2: ({ children }) => (
          <h2 className="text-xl font-bold mb-3 mt-5 text-foreground">
            {children}
          </h2>
        ),
        h3: ({ children }) => (
          <h3 className="text-lg font-semibold mb-2 mt-4 text-foreground">
            {children}
          </h3>
        ),
        h4: ({ children }) => (
          <h4 className="text-base font-semibold mb-2 mt-3 text-foreground">
            {children}
          </h4>
        ),
        ul: ({ children }) => (
          <ul className="list-disc ml-6 mb-4 space-y-1">
            {children}
          </ul>
        ),
        ol: ({ children }) => (
          <ol className="list-decimal ml-6 mb-4 space-y-1">
            {children}
          </ol>
        ),
        li: ({ children }) => (
          <li className="leading-relaxed">
            {children}
          </li>
        ),
        blockquote: ({ children }) => (
          <blockquote className="border-l-4 border-primary pl-4 ml-2 mb-4 italic bg-muted/50 py-2 rounded-r">
            {children}
          </blockquote>
        ),
        strong: ({ children }) => (
          <strong className="font-semibold text-foreground">
            {children}
          </strong>
        ),
        em: ({ children }) => (
          <em className="italic">
            {children}
          </em>
        ),
        hr: () => (
          <Separator className="my-6" />
        ),
        table: ({ children }) => (
          <div className="overflow-x-auto my-4">
            <table className="w-full border-collapse border border-border rounded-lg">
              {children}
            </table>
          </div>
        ),
        th: ({ children }) => (
          <th className="border border-border bg-muted px-3 py-2 text-left font-semibold">
            {children}
          </th>
        ),
        td: ({ children }) => (
          <td className="border border-border px-3 py-2">
            {children}
          </td>
        ),
      }}
    >
      {content}
    </ReactMarkdown>
  );

  const ProgressUpdateMessage = ({ data }: { data: any }) => (
    <Accordion type="single" collapsible className="border rounded-lg">
      <AccordionItem value="progress">
        <AccordionTrigger className="py-3 px-4 hover:no-underline">
          <div className="flex items-center space-x-3 w-full">
            <Loader2 className="w-4 h-4 animate-spin text-primary" />
            <span className="text-sm text-left flex-1">
              {data.text || data.message || 'Processing...'}
            </span>
            <span className="text-xs text-muted-foreground">
              {new Date(data.timestamp || Date.now()).toLocaleTimeString()}
            </span>
          </div>
        </AccordionTrigger>
        <AccordionContent className="px-4 pb-4">
          <div className="space-y-3">
            {data.data?.progress !== undefined && (
              <div>
                <Progress value={data.data.progress * 100} className="mb-2" />
                <p className="text-xs text-muted-foreground">
                  Progress: {Math.round(data.data.progress * 100)}%
                </p>
              </div>
            )}
            <p className="text-xs text-muted-foreground">
              Stage: {data.data?.stage || 'processing'}
            </p>
          </div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );

  const renderMessageContent = (msg: Message) => {
    if (msg.type === 'progress_update') {
      return <ProgressUpdateMessage data={msg} />;
    } else if (msg.type === 'clarifying_questions') {
      const questions = msg.data || {};
      const introText = msg.text || "I need some clarification to provide you with the best research:";
      
      return (
        <div className="space-y-4">
          <Card className="shadow-md border-0 bg-white dark:bg-gray-800">
            <CardContent className="p-6 space-y-4">
              <p className="text-gray-700 dark:text-gray-200 font-medium">{introText}</p>
              
              <div className="space-y-3">
                <div className="space-y-3">
                  {Object.entries(questions).map(([, question], index) => (
                    <div key={index} className="flex items-start space-x-3">
                      <span className="flex-shrink-0 text-gray-500 dark:text-gray-400 font-medium">
                        {index + 1}.
                      </span>
                      <p className="text-gray-700 dark:text-gray-200">{question as string}</p>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="pt-4">
                <p className="text-xs text-gray-400 dark:text-gray-500">
                  Type your answers in the chat box below and press Enter to continue.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    } else if (msg.type === 'research_complete' || msg.type === 'summary_retry_complete') {
      return <ResearchCompleteResult data={msg.data} />;
    } else if (msg.type === 'qa_response') {
      return <QAResponseResult data={msg.data} />;
    } else if (msg.type === 'comprehensive_research_result') {
      return <ComprehensiveResearchResult data={msg.data} />;
    } else if (msg.type === 'knowledge_based_response') {
      return (
        <div>
          <Card className="shadow-sm border-0 bg-gray-50 dark:bg-gray-800 mb-4">
            <CardContent className="p-3">
              <p className="text-xs text-gray-500 dark:text-gray-400">
                This response is based on my existing knowledge base
              </p>
            </CardContent>
          </Card>
          <div className="prose prose-sm max-w-none dark:prose-invert">
            <MarkdownRenderer content={msg.text} />
          </div>
        </div>
      );
    } else if (msg.type === 'query_validation_response') {
      return <QueryValidationResult data={msg.data} text={msg.text} />;
    } else if (msg.isUser) {
      return (
        <div className="prose prose-sm max-w-none dark:prose-invert prose-p:mb-2">
          <p>{msg.text}</p>
        </div>
      );
    } else {
      return (
        <div className="prose prose-sm max-w-none dark:prose-invert">
          <MarkdownRenderer content={msg.text} />
        </div>
      );
    }
  };

  // Show loading state when loading existing session
  if (loadingSession) {
    return (
      <div className="flex flex-col h-screen items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-sky-600 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
            Loading Research Data
          </h3>
          <p className="text-gray-600 dark:text-gray-400">
            Retrieving your previous research session...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full">
      {/* Messages */}
      <div className="pb-24">
        <div className="max-w-4xl mx-auto p-4 space-y-4">
          {messages.map((msg) => (
            <div
              key={msg.id}
              className={cn(
                "flex",
                msg.isUser ? "justify-end" : "justify-start"
              )}
            >
              <div
                className={cn(
                  "max-w-[85%] rounded-2xl p-4 shadow-md border",
                  msg.isUser
                    ? "bg-white dark:bg-gradient-to-r dark:from-blue-600 dark:to-sky-500 text-gray-900 dark:text-white rounded-br-md"
                    : "bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-bl-md"
                )}
              >
                {renderMessageContent(msg)}
              </div>
            </div>
          ))}
          
          {/* Summary Versions - Hide during thinking state */}
          {summaryVersions.length > 0 && !thinkingState.isThinking && (
            <SummaryVersions versions={summaryVersions} />
          )}
          
          {/* Thinking Component */}
          <ThinkingComponent />
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Input Area - Clean bottom positioning like Claude */}
      <div className={cn(
        "fixed bottom-0 left-0 right-0 p-4 bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm border-t border-gray-200 dark:border-gray-700 z-20 transition-all duration-300",
        // Responsive left margin based on sidebar state - only on desktop
        "lg:left-0",
        !sidebarCollapsed && "lg:left-[280px]",
        sidebarCollapsed && "lg:left-[80px]"
      )}>
        <div className="max-w-4xl mx-auto">
          <div className="flex space-x-3 items-end">
            <div className="flex-1 relative">
              <Textarea
                placeholder="Ask me anything about your research..."
                value={input}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                disabled={isLoading}
                className="min-h-[52px] max-h-[200px] resize-none pr-16 border border-gray-300 dark:border-gray-600 rounded-xl focus:border-sky-400 dark:focus:border-sky-500 transition-colors bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder:text-gray-500 dark:placeholder:text-gray-400 focus:ring-1 focus:ring-sky-400 dark:focus:ring-sky-500"
              />
              <div className="absolute bottom-2 right-2 flex items-center space-x-2">
                <span 
                  className={`text-xs text-gray-500 dark:text-gray-400 transition-opacity duration-200 ${
                    isInputEmpty ? 'opacity-0' : 'opacity-100'
                  }`}
                >
                  Press Enter to send
                </span>
              </div>
            </div>
            <Button
              onClick={() => {
                console.log('DEBUG: Send button clicked, calling handleSend()');
                handleSend();
              }}
              disabled={isSendDisabled}
              size="lg"
              className="h-[52px] px-4 rounded-xl bg-gradient-to-r from-blue-600 to-sky-500 dark:from-blue-600 dark:to-sky-500 hover:from-blue-700 hover:to-sky-600 dark:hover:from-blue-700 dark:hover:to-sky-600 text-white transition-colors"
            >
              {isLoading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Send className="w-5 h-5" />
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatInterface;
